import { configureFeatures } from "@spark/features";
import { deepFreeze, telemetryServiceName } from "@spark/domain";
import { openobserveLogs } from "@openobserve/browser-logs";
import { openobserveRum } from "@openobserve/browser-rum";

//#region src/features.ts
const injected = typeof window !== "undefined" && "__spark_features__" in window ? window.__spark_features__ : void 0;
if (Array.isArray(injected)) configureFeatures(Object.fromEntries(injected.filter((feature) => typeof feature === "string").map((feature) => [feature, true])));

//#endregion
//#region src/config.ts
const config = deepFreeze((typeof window !== "undefined" && "__spark_config__" in window ? window.__spark_config__ : {}) ?? {});
function getFrontendConfig(key) {
	return key ? config[key] : config;
}
/**
* @throws If no frontend configuration is found.
* @returns A function to get the frontend configuration.
*/
function frontendConfigure() {
	if ("__spark_config__" in window === false) throw new Error("No frontend configuration found. Make sure to run the whole Spark server to include the frontend middleware.");
	return getFrontendConfig;
}

//#endregion
//#region src/debug.ts
const COOKIE_NAME = "SPARK_DEBUG";
const COOKIE_VALUE = "1";
const COOKIE_MAX_AGE_SECONDS = 2592e3;
const listeners = /* @__PURE__ */ new Set();
/**
* Whether Spark debug tooling is enabled, based on the presence of the
* `SPARK_DEBUG` cookie. Cheap and synchronous, safe to call at boot.
*/
function isSparkDebug() {
	if (typeof document === "undefined") return false;
	return document.cookie.split(";").some((cookie) => cookie.trim().startsWith(`${COOKIE_NAME}=`));
}
/**
* Enable or disable Spark debug tooling by writing or deleting the
* `SPARK_DEBUG` cookie on the root domain, so other Spark services on the
* same domain can pick it up. Notifies same-tab listeners afterwards.
*
* The cookie carries no identity or trust; it only opts the UI into debug
* tooling.
*/
async function setSparkDebug(enabled) {
	if (enabled) await writeCookie();
	else await deleteCookie();
	for (const listener of [...listeners]) listener(enabled);
}
/**
* Subscribe to same-tab debug flag changes made through {@link setSparkDebug}.
* @returns An unsubscribe function.
*/
function onSparkDebugChange(callback) {
	listeners.add(callback);
	return () => listeners.delete(callback);
}
/**
* Define the `configSpark` global, the console-facing API to toggle Spark
* debug tooling: `configSpark({ debug: true })`. Idempotent.
*/
function registerConfigSpark() {
	if (globalThis.configSpark) return;
	globalThis.configSpark = async (options) => {
		if (options.debug !== void 0) {
			await setSparkDebug(options.debug);
			console.info(`[spark] debug ${options.debug ? "enabled" : "disabled"}`);
		}
	};
}
function getHostname() {
	return typeof window === "undefined" ? "" : window.location.hostname;
}
/**
* Candidate cookie domains, broadest first (e.g. `blink.nl`, `app.blink.nl`,
* `player.app.blink.nl`). Empty for hosts that need a host-only cookie
* (localhost, single labels, IP addresses).
*/
function candidateDomains() {
	const hostname = getHostname();
	const labels = hostname.split(".");
	const isIp = hostname.startsWith("[") || /^[\d.]+$/.test(hostname);
	if (labels.length < 2 || isIp) return [];
	const domains = [];
	for (let i = labels.length - 2; i >= 0; i--) domains.push(labels.slice(i).join("."));
	return domains;
}
async function writeCookie() {
	const domains = candidateDomains();
	if (typeof cookieStore === "undefined") {
		writeDocumentCookie(domains[0]);
		return;
	}
	for (const domain of domains) try {
		await set(domain);
		return;
	} catch {}
	await set(void 0);
	function set(domain) {
		return cookieStore.set({
			name: COOKIE_NAME,
			value: COOKIE_VALUE,
			domain,
			path: "/",
			sameSite: "lax",
			expires: Date.now() + COOKIE_MAX_AGE_SECONDS * 1e3
		});
	}
}
async function deleteCookie() {
	const domains = candidateDomains();
	if (typeof cookieStore === "undefined") {
		expireDocumentCookie(void 0);
		for (const domain of domains) expireDocumentCookie(domain);
		return;
	}
	await cookieStore.delete({
		name: COOKIE_NAME,
		path: "/"
	});
	for (const domain of domains) try {
		await cookieStore.delete({
			name: COOKIE_NAME,
			domain,
			path: "/"
		});
	} catch {}
}
function writeDocumentCookie(domain) {
	document.cookie = `${COOKIE_NAME}=${COOKIE_VALUE}; path=/; samesite=lax; max-age=${COOKIE_MAX_AGE_SECONDS}` + (domain ? `; domain=${domain}` : "");
}
function expireDocumentCookie(domain) {
	document.cookie = `${COOKIE_NAME}=; path=/; samesite=lax; max-age=0` + (domain ? `; domain=${domain}` : "");
}

//#endregion
//#region src/telemetry-log.ts
function telemetryLog(message, ...details) {
	console.debug(`[spark:rum] ${message}`, ...details);
}

//#endregion
//#region src/otlp.ts
const OtlpSpanKind = {
	internal: 1,
	server: 2,
	client: 3
};
const OtlpStatusCode = {
	unset: 0,
	ok: 1,
	error: 2
};
const OtlpSeverityNumber = {
	info: 9,
	warn: 13,
	error: 17
};
const BATCH_SIZE = 50;
const FLUSH_DELAY_MS = 5e3;
function otlpAttributes(input) {
	const attributes = [];
	for (const [key, value] of Object.entries(input)) {
		if (value === void 0 || value === null || value === "") continue;
		attributes.push({
			key,
			value: otlpAttributeValue(value)
		});
	}
	return attributes;
}
function otlpTraceId() {
	return randomHex(16);
}
function otlpSpanId() {
	return randomHex(8);
}
function randomHex(bytes) {
	const buffer = new Uint8Array(bytes);
	crypto.getRandomValues(buffer);
	return Array.from(buffer, (byte) => byte.toString(16).padStart(2, "0")).join("");
}
function nanosFromMillis(millis) {
	return (BigInt(Math.round(millis)) * 1000000n).toString();
}
function nanosPlus(nanos, offsetNanos) {
	return (BigInt(nanos) + BigInt(Math.round(offsetNanos))).toString();
}
function onPageHidden(handler) {
	if (typeof document !== "undefined") document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "hidden") handler();
	});
	if (typeof window !== "undefined") window.addEventListener("pagehide", handler);
}
function createOtlpExporter(options) {
	const { endpoint, headers, resource, scope } = options;
	const spans = [];
	const logs = [];
	let timer;
	return {
		addSpan(span) {
			spans.push(span);
			schedule();
		},
		addLog(record) {
			logs.push(record);
			schedule();
		},
		flush
	};
	function schedule() {
		if (spans.length + logs.length >= BATCH_SIZE) {
			flush();
			return;
		}
		timer ??= setTimeout(() => flush(), FLUSH_DELAY_MS);
	}
	function flush({ keepalive = false } = {}) {
		if (timer !== void 0) {
			clearTimeout(timer);
			timer = void 0;
		}
		if (spans.length > 0) post("v1/traces", keepalive, { resourceSpans: [{
			resource: { attributes: resource },
			scopeSpans: [{
				scope,
				spans: spans.splice(0, spans.length)
			}]
		}] });
		if (logs.length > 0) post("v1/logs", keepalive, { resourceLogs: [{
			resource: { attributes: resource },
			scopeLogs: [{
				scope,
				logRecords: logs.splice(0, logs.length)
			}]
		}] });
	}
	function post(path, keepalive, payload) {
		fetch(`${endpoint}/${path}`, {
			method: "POST",
			headers: {
				"content-type": "application/json",
				...headers
			},
			body: JSON.stringify(payload),
			credentials: "omit",
			keepalive
		}).then((response) => {
			if (!response.ok) telemetryLog(`${path} rejected`, response.status);
		}, (error) => telemetryLog(`${path} failed`, error));
	}
}
function otlpAttributeValue(value) {
	if (typeof value === "boolean") return { boolValue: value };
	if (typeof value === "string") return { stringValue: value };
	return Number.isInteger(value) ? { intValue: `${value}` } : { doubleValue: value };
}

//#endregion
//#region src/rum-config.ts
function oneuptimeOrigin(config) {
	if (!config.oneuptime?.token || !config.site) return;
	return `${config.insecureHTTP ? "http" : "https"}://${config.site}`;
}
function oneuptimeEndpoint(config) {
	const origin = oneuptimeOrigin(config);
	if (!origin) return;
	return `${origin}${(config.oneuptime?.path || "/otlp").replace(/\/$/, "")}`;
}
function peerService(url, peers) {
	if (!peers) return;
	return peers[resolvedOrigin(url)];
}
function resolvedOrigin(url) {
	try {
		return new URL(url, typeof window === "undefined" ? void 0 : window.location.href).origin;
	} catch {
		return "";
	}
}

//#endregion
//#region src/session.ts
const session = typeof window !== "undefined" && "__spark_session__" in window ? deepFreeze(window.__spark_session__) : false;
/**
* Read frontend session information.
*/
function getSession() {
	return session;
}

//#endregion
//#region src/session-replay.ts
const LOADER_PATH = "/telemetry/session-replay/v1/recorder.js";
const REQUIRED_CONSENT = "analytics";
let recordedSessionId;
function replaySessionId() {
	return recordedSessionId;
}
function registerSessionReplay(config) {
	const { oneuptime } = config;
	const host = oneuptimeOrigin(config);
	if (!config.replay || !oneuptime || !host) {
		telemetryLog("OneUptime session replay disabled");
		return;
	}
	const session = getSession();
	if (!session) {
		telemetryLog("OneUptime session replay skipped without a session");
		return;
	}
	if (!session.legal.consent.includes(REQUIRED_CONSENT)) {
		telemetryLog(`OneUptime session replay withheld without '${REQUIRED_CONSENT}' consent`);
		return;
	}
	const loader = `${host}${LOADER_PATH}`;
	telemetryLog(`OneUptime session replay initializing from ${loader}`);
	const target = window;
	target.__ONEUPTIME_SESSION_REPLAY__ = {
		host,
		token: oneuptime.token,
		appIdentifier: config.service,
		userRef: session.user.id
	};
	target.OneUptimeReplayQueue ??= [];
	target.OneUptimeReplayQueue.push(["onSessionChange", (sessionId) => rememberSession(sessionId)], [
		"identify",
		session.user.id,
		userTraits(session)
	], ["setTags", { "spark.session.id": session.id }]);
	const script = document.createElement("script");
	script.src = loader;
	script.async = true;
	script.crossOrigin = "anonymous";
	document.head.appendChild(script);
}
function rememberSession(sessionId) {
	recordedSessionId = sessionId ?? void 0;
}
function userTraits(session) {
	const { user } = session;
	if (user.kind === "internal") return { kind: user.kind };
	return {
		kind: user.kind,
		school: user.school.brin ?? user.school.id,
		"school.name": user.school.name
	};
}

//#endregion
//#region src/oneuptime.ts
function registerOneUptimeRum(config) {
	const endpoint = oneuptimeEndpoint(config);
	const token = config.oneuptime?.token;
	if (!endpoint || !token) {
		telemetryLog("OneUptime RUM disabled");
		return;
	}
	const exporter = createOtlpExporter({
		endpoint,
		headers: { "x-oneuptime-token": token },
		resource: otlpAttributes({
			"service.name": config.service,
			"service.version": config.version,
			"deployment.environment.name": config.env,
			"telemetry.sdk.name": "spark-frontend-rum",
			"telemetry.sdk.language": "webjs",
			...browserAttributes()
		}),
		scope: {
			name: "@spark/frontend-config",
			version: config.version
		}
	});
	telemetryLog(`OneUptime RUM initializing, sending to ${endpoint}`);
	const views = /* @__PURE__ */ new Map();
	onPageHidden(() => {
		for (const id of views.keys()) endView(id);
		exporter.flush({ keepalive: true });
	});
	return {
		mirror,
		flush: () => exporter.flush()
	};
	function mirror(event, context) {
		if (event.type === "resource") {
			mirrorResource(exporter, config, event, context);
			return;
		}
		if (event.type === "error") {
			mirrorError(exporter, event, viewTrace(event.view?.id));
			return;
		}
		if (event.type === "view") trackView(event);
	}
	function viewTrace(id) {
		if (id === void 0) return;
		const tracked = views.get(id) ?? {
			traceId: otlpTraceId(),
			spanId: otlpSpanId(),
			loaded: false,
			ended: false
		};
		views.set(id, tracked);
		return tracked;
	}
	function trackView(event) {
		const { view } = event;
		const tracked = viewTrace(view.id);
		if (!tracked || tracked.ended) return;
		tracked.latest = event;
		const { loading_time } = view;
		if (!tracked.loaded && loading_time !== void 0) {
			tracked.loaded = true;
			mirrorViewLoad(exporter, tracked, event, loading_time);
		}
		if (view.is_active === false) endView(view.id);
	}
	function endView(id) {
		const tracked = views.get(id);
		if (!tracked || tracked.ended) return;
		tracked.ended = true;
		if (tracked.latest) mirrorViewEnd(exporter, tracked, tracked.latest);
	}
}
function browserAttributes() {
	if (typeof navigator === "undefined") return {};
	const { userAgentData } = navigator;
	return {
		"browser.language": navigator.language,
		"browser.platform": userAgentData?.platform || navigator.platform,
		"browser.mobile": userAgentData?.mobile,
		"user_agent.original": navigator.userAgent
	};
}
function mirrorViewLoad(exporter, tracked, event, loadingTime) {
	const { view } = event;
	const start = nanosFromMillis(event.date);
	exporter.addSpan({
		traceId: tracked.traceId,
		spanId: otlpSpanId(),
		parentSpanId: tracked.spanId,
		name: `${view.loading_type ?? "load"} ${viewPath(event)}`,
		kind: OtlpSpanKind.internal,
		startTimeUnixNano: start,
		endTimeUnixNano: nanosPlus(start, loadingTime),
		attributes: otlpAttributes({
			...sharedAttributes(event),
			...loadAttributes(view)
		})
	});
}
function mirrorViewEnd(exporter, tracked, event) {
	const { view } = event;
	const start = nanosFromMillis(event.date);
	exporter.addSpan({
		traceId: tracked.traceId,
		spanId: tracked.spanId,
		name: `view ${viewPath(event)}`,
		kind: OtlpSpanKind.internal,
		startTimeUnixNano: start,
		endTimeUnixNano: nanosPlus(start, view.time_spent),
		attributes: otlpAttributes({
			...sharedAttributes(event),
			...loadAttributes(view),
			"view.loading_time": view.loading_time,
			"view.time_spent": view.time_spent,
			"view.interaction_to_next_paint": view.interaction_to_next_paint,
			"view.cumulative_layout_shift": view.cumulative_layout_shift,
			"view.error.count": view.error.count,
			"view.resource.count": view.resource.count,
			"view.action.count": view.action.count,
			"view.long_task.count": view.long_task?.count
		})
	});
}
function loadAttributes(view) {
	return {
		"view.loading_type": view.loading_type,
		"view.first_byte": view.first_byte,
		"view.first_contentful_paint": view.first_contentful_paint,
		"view.largest_contentful_paint": view.largest_contentful_paint,
		"view.dom_interactive": view.dom_interactive,
		"view.dom_content_loaded": view.dom_content_loaded,
		"view.load_event": view.load_event
	};
}
function viewPath(event) {
	return parseUrl(event.view.url)?.pathname ?? event.view.url;
}
function mirrorResource(exporter, config, event, context) {
	const traceId = event._o2?.trace_id;
	const spanId = event._o2?.span_id;
	if (!traceId || !spanId) return;
	const { resource } = event;
	const method = resource.method ?? "GET";
	const target = parseUrl(resource.url);
	const status = resource.status_code && resource.status_code > 0 ? resource.status_code : void 0;
	const failure = status === void 0 ? requestFailure(context) : void 0;
	const start = nanosFromMillis(event.date);
	exporter.addSpan({
		traceId,
		spanId,
		name: target ? `${method} ${target.pathname}` : method,
		kind: OtlpSpanKind.client,
		startTimeUnixNano: start,
		endTimeUnixNano: nanosPlus(start, resource.duration ?? 0),
		attributes: otlpAttributes({
			...sharedAttributes(event),
			"http.request.method": method,
			"http.response.status_code": status,
			"url.full": resource.url,
			"url.scheme": target?.protocol.replace(/:$/, ""),
			"server.address": target?.hostname,
			"peer.service": peerService(resource.url, config.peers),
			"error.type": failure?.errorType
		}),
		status: spanStatus(status, failure)
	});
}
function spanStatus(status, failure) {
	if (status !== void 0) return status >= 400 ? {
		code: OtlpStatusCode.error,
		message: `HTTP ${status}`
	} : void 0;
	if (!failure || failure.aborted) return;
	return {
		code: OtlpStatusCode.error,
		message: failure.message
	};
}
function requestFailure(context) {
	if (!("isManual" in context) || context.isManual !== false) return;
	const { error, isAborted } = context;
	return isAborted ? {
		aborted: true,
		errorType: error?.name || "AbortError",
		message: error?.message || "Request aborted"
	} : {
		aborted: false,
		errorType: error?.name || "network_error",
		message: error?.message || "Request failed without a response"
	};
}
function mirrorError(exporter, event, view) {
	const { error } = event;
	const trace = errorTrace(event, view);
	exporter.addLog({
		timeUnixNano: nanosFromMillis(event.date),
		observedTimeUnixNano: nanosFromMillis(Date.now()),
		severityNumber: OtlpSeverityNumber.error,
		severityText: "ERROR",
		body: { stringValue: error.message },
		traceId: trace?.traceId,
		spanId: trace?.spanId,
		attributes: otlpAttributes({
			...sharedAttributes(event),
			"exception.type": error.type,
			"exception.message": error.message,
			"exception.stacktrace": error.stack,
			"error.source": error.source,
			"error.handling": error.handling,
			"url.full": error.resource?.url,
			"http.request.method": error.resource?.method,
			"http.response.status_code": error.resource?.status_code || void 0
		})
	});
}
function errorTrace(event, view) {
	const traceId = event._o2?.trace_id;
	const spanId = event._o2?.span_id;
	if (traceId && spanId) return {
		traceId,
		spanId
	};
	return view && {
		traceId: view.traceId,
		spanId: view.spanId
	};
}
function sharedAttributes(event) {
	const sparkSession = event.context?.spark_session_id;
	return {
		"session.id": replaySessionId() ?? event.session.id,
		"rum.session.id": event.session.id,
		"spark.session.id": typeof sparkSession === "string" ? sparkSession : void 0,
		"view.id": event.view?.id,
		"page.url": event.view?.url,
		"user.id": event.usr?.id
	};
}
function parseUrl(url) {
	try {
		return new URL(url, typeof window === "undefined" ? void 0 : window.location.href);
	} catch {
		return;
	}
}

//#endregion
//#region src/openobserve.ts
function isSameOrigin(url) {
	return new URL(url, window.location.href).origin === window.location.origin;
}
function tracedUrl(config) {
	return (url) => isSameOrigin(url) || peerService(url, config.peers) !== void 0;
}
function registerOpenObserveRum(config, beforeSend) {
	telemetryLog(`OpenObserve RUM initializing, sending to ${config.site}`);
	const connection = {
		clientToken: config.clientToken,
		site: config.site,
		organizationIdentifier: config.organizationIdentifier,
		apiVersion: config.apiVersion,
		insecureHTTP: config.insecureHTTP,
		service: config.service,
		env: config.env,
		version: config.version
	};
	const sessionSampleRate = config.sampleRate ?? 100;
	const sessionReplaySampleRate = config.replay ? config.replayRate ?? 100 : 0;
	openobserveRum.init({
		...connection,
		applicationId: config.applicationId,
		sessionSampleRate,
		sessionReplaySampleRate,
		defaultPrivacyLevel: "mask",
		trackResources: true,
		trackLongTasks: true,
		trackUserInteractions: false,
		traceSampleRate: 100,
		allowedTracingUrls: [{
			match: tracedUrl(config),
			propagatorTypes: ["tracecontext"]
		}],
		beforeSend
	});
	openobserveLogs.init({
		...connection,
		sessionSampleRate,
		forwardErrorsToLogs: true
	});
	const session = getSession();
	if (session) {
		const { user } = session;
		openobserveRum.setUser({ id: user.id });
		if (user.kind !== "internal") openobserveRum.setAccount({
			id: user.school.brin ?? user.school.id,
			name: user.school?.name
		});
		openobserveRum.setGlobalContextProperty("spark_session_id", session.id);
		for (const permission of [
			"functional",
			"limited-analytics",
			"analytics",
			"marketing"
		]) openobserveRum.setGlobalContextProperty(`consent.${permission}`, session.legal.consent.includes(permission));
	} else {
		openobserveRum.clearUser();
		openobserveRum.clearAccount();
		openobserveRum.removeGlobalContextProperty("spark_session_id");
	}
}
function addOpenObserveError(error, context) {
	openobserveRum.addError(error, context);
}

//#endregion
//#region src/rum.ts
const getConfig = frontendConfigure();
function registerRum() {
	const configured = getConfig("rum");
	if (!configured?.site || !configured.clientToken) {
		telemetryLog("RUM disabled");
		return;
	}
	const config = {
		...configured,
		service: telemetryServiceName(typeof __PACKAGE_NAME__ !== "undefined" ? __PACKAGE_NAME__ : configured.service),
		version: configured.version.toLowerCase().replace(/\+/g, "-"),
		replay: configured.replay === true && !recordingStudent()
	};
	const oneuptime = registerOneUptimeRum(config);
	registerSessionReplay(config);
	const telemetryOrigin = oneuptimeOrigin(config);
	registerOpenObserveRum(config, (event, context) => {
		if (isOwnTelemetry(event, telemetryOrigin)) return false;
		attributePeerService(event, config);
		oneuptime?.mirror(event, context);
		return true;
	});
}
function reportContractClientError(event) {
	if (event.kind !== "response") return;
	addOpenObserveError(event.error, { contract: {
		method: event.request.method,
		path: event.request.path,
		url: event.request.url,
		status: event.response.status
	} });
}
function recordingStudent() {
	const session = getSession();
	if (session === false || session.user.kind !== "student") return false;
	telemetryLog("Session replay withheld from a student session");
	return true;
}
function isOwnTelemetry(event, telemetryOrigin) {
	return telemetryOrigin !== void 0 && event.type === "resource" && event.resource.url.startsWith(telemetryOrigin);
}
function attributePeerService(event, config) {
	if (event.type !== "resource") return;
	const peer = peerService(event.resource.url, config.peers);
	if (!peer) return;
	event.context = {
		...event.context,
		"peer.service": peer
	};
}

//#endregion
export { frontendConfigure, getSession, isSparkDebug, onSparkDebugChange, registerConfigSpark, registerRum, reportContractClientError, setSparkDebug };